import React, { useRef } from 'react';
import { Camera, ImagePlus, PenLine, Images } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

export const UploadStep = ({
  cameraInputRef, fileInputRef, handleFileSelect,
  onManualEntry, handleMultiFileSelect,
}) => {
  const { t } = useLanguage();
  // Dedicated hidden input for the "one plant, many photos" flow. Kept
  // separate from `fileInputRef` so the batch-add multi-select and the
  // single-plant multi-photo select can each own their own change handler.
  const multiFileInputRef = useRef(null);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div
          onClick={() => cameraInputRef.current?.click()}
          className="border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer hover:border-primary transition-colors"
          style={{ borderColor: 'hsl(143, 28%, 70%)', backgroundColor: 'hsl(82, 55%, 98%)' }}
          data-testid="camera-area"
        >
          <Camera className="w-12 h-12 mx-auto mb-3" style={{ color: 'hsl(143, 28%, 25%)', opacity: 0.5 }} />
          <p className="text-base font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>{t('fromCamera')}</p>
        </div>
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer hover:border-primary transition-colors"
          style={{ borderColor: 'hsl(143, 28%, 70%)', backgroundColor: 'hsl(82, 55%, 98%)' }}
          data-testid="upload-area"
        >
          <ImagePlus className="w-12 h-12 mx-auto mb-3" style={{ color: 'hsl(143, 28%, 25%)', opacity: 0.5 }} />
          <p className="text-base font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>{t('fromGallery')}</p>
        </div>
      </div>

      {/* Multi-photo of ONE plant. Distinct from the 2-tile batch flow above
          (which creates one plant per photo). Sends all photos to the AI
          together so it can identify from multiple angles/close-ups. */}
      <div
        onClick={() => multiFileInputRef.current?.click()}
        className="border-2 border-dashed rounded-xl px-4 py-4 flex items-center gap-3 cursor-pointer hover:border-primary transition-colors"
        style={{ borderColor: 'hsl(143, 28%, 70%)', backgroundColor: 'hsl(82, 55%, 98%)' }}
        data-testid="multi-photo-area"
      >
        <Images className="w-8 h-8 flex-shrink-0" style={{ color: 'hsl(143, 28%, 25%)', opacity: 0.6 }} />
        <div className="flex-1 min-w-0 text-left">
          <p className="text-sm font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>
            {t('multiPhotoTitle') || 'Add plant with multiple photos'}
          </p>
          <p className="text-xs" style={{ color: 'hsl(125, 12%, 40%)' }}>
            {t('multiPhotoDesc') || 'Send several angles of the same plant so the AI identifies it more accurately. Up to 5 photos.'}
          </p>
        </div>
      </div>

      <div className="text-center pt-1">
        <button
          onClick={onManualEntry}
          className="inline-flex items-center gap-1.5 text-sm cursor-pointer hover:underline transition-colors"
          style={{ color: 'hsl(125, 12%, 40%)' }}
          data-testid="manual-entry-button"
        >
          <PenLine className="w-3.5 h-3.5" />
          {t('enterManually')}
        </button>
      </div>
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" />
      <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileSelect} className="hidden" />
      <input
        ref={multiFileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleMultiFileSelect}
        className="hidden"
        data-testid="multi-photo-input"
      />
    </div>
  );
};
